import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, NgZone, PLATFORM_ID, afterNextRender, effect, inject, signal, untracked, DOCUMENT } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { SwUpdate } from '@angular/service-worker';
import { of, timer } from 'rxjs';
// import { SocketService } from './services/socket.service';
import { environment } from '@env';
import { EventService } from './services/event.service';
import { StorageService } from './services/storage.service';
import { ApiService } from './services/api.service';


@Component({
  selector: '[bts-root]',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  host: { time: new Date().getTime().toString() },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  title = 'BlueTees-PreGame';
  // Dependency Injection
  private readonly event = inject(EventService);
  private readonly storage = inject(StorageService);
  private readonly api = inject(ApiService);
  // private readonly socketService = inject(SocketService);


  private readonly router = inject(Router);
  private readonly swUpdate = inject(SwUpdate)
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Local Variables

  protected updateAvailable = signal(false);
  protected isOnline = signal(true);


  // rxResource

  readonly userDetails = rxResource({
    params: () => this.event.isLoggedIn(),
    stream: ({ params }) => {
      if (!this.isBrowser) return of(null);
      return params ? this.api.get(`/user-details`) : (of(false)) //this.socketService.disconnect() future ref
    }
  });


  constructor() {
    //  const data = [  ].map((item) => {
    //     return {
    //       continentL: item.continent.name.trim().replaceAll(' ', '-').toLowerCase(),
    //       country: item.country.name.trim().replaceAll(' ', '-').toLowerCase(),
    //       region: item?.region?.name.trim().replaceAll(' ', '-').toLowerCase() || '',
    //       slug: item.slug
    //     }
    //   });

    //   console.log("data: ", data);


    /* The code `this.zone.runOutsideAngular(() => { this.document.body.addEventListener('focus', () =>
    { this.socketService.connect(this.storage.getDataField('token')); }) })` is setting up an event
    listener for the 'focus' event on the body element of the document. When the body element
    receives focus, it will trigger the callback function that connects to a socket service using
    the token retrieved from the storage. */
    // this.zone.runOutsideAngular(() => {
    //   this.document.body.addEventListener('focus', () => {
    //     this.socketService.connect(this.storage.getDataField('token'));
    //   })
    // })
    //future ref socket


    effect(() => {
      const userDetails = this.userDetails.value();
      if (userDetails) {
        this.event.user.set(userDetails.data);
      }
      const error: any = this.userDetails.error();
      if (error) {
        this.api.logout();
        // this.socketService.disconnect(); future ref 
        const err = error?.cause?.error;
        err && this.api.alert(err?.message, 'warning');
      }
    })

    const excluded = ['/login', '/signup', '/verify', '/report']
    const _router = toSignal(this.router.events);

    effect(() => {
      const router = _router();
      untracked(() => {
        if (router instanceof NavigationCancel && this.isBrowser && !this.event.isLoggedIn()) this.router.navigate(['/login']);
        if (router instanceof NavigationEnd) {
          this.setCanonicalURL(`${environment.SITE_URL}${router.url}`);
        }
        if (router instanceof NavigationCancel && this.isBrowser && this.event.isLoggedIn()) {
          this.router.navigate(['/'])
        };
        if (router instanceof NavigationEnd && this.isBrowser) {
          if (!excluded.includes(router.url)) {
            this.zone.runOutsideAngular(() => {
              this.document.body.scrollTo({ top: 0, 'behavior': 'smooth' });
            })
          }
        }

        if (router instanceof NavigationStart && this.isBrowser) {
          if (!excluded.includes(router.url)) {
            const URL = router.url
            URL && this.event.currentURL.set(decodeURIComponent(URL));
          }
        }
      })
    });


    afterNextRender(() => {
      this.zone.runOutsideAngular(() => {
        window.addEventListener('offline', () => {
          this.isOnline.set(false)
        });
        window.addEventListener('online', () => {
          this.isOnline.set(true)
        })

        if (this.swUpdate.isEnabled) {
          this.swUpdate.checkForUpdate().then((e: boolean) => {
            this.updateAvailable.set(e);
          });
          this.swUpdate.versionUpdates.subscribe((res: any) => {
            res.type !== "VERSION_READY" && console.log("Site already Updated");
            res && res.type === "VERSION_READY" && res.currentVersion.hash !== res.latestVersion.hash && (this.updateAvailable.set(true))
          })
        }

        setTimeout(() => {
          requestAnimationFrame(() => {
            const bodyRect = this.document.body.getBoundingClientRect();
            // this.document.body.style.setProperty('--height', `${bodyRect.height.toFixed(0)}px`);
            this.document.documentElement.style.setProperty('--height', `${bodyRect.height.toFixed(0)}px`);
            if (this.isSafari()) {
              this.document.body.classList.add('safari-browser')
            }
          })
        }, 0)

      });
    });

  }

  /* The `@HostListener('document:fullscreenchange', [])` decorator in the `AppComponent` class is
  setting up an event listener for the `fullscreenchange` event on the `document` object. When the
  `fullscreenchange` event is triggered, the `onFullscreenChange` method is called with the event
  object as a parameter. */
  @HostListener('document:fullscreenchange', ['$event'])
  onFullscreenChange(event: any): void {
    this.zone.runOutsideAngular(() => {
      this.document.body.style.setProperty('--height', `${event.target.innerHeight}px`);
    })
  }

  /**
   * The `onResize` function in TypeScript sets the height of a modal based on the inner height of the
   * target element.
   * @param {any} event - The `event` parameter in the `onResize` function is an object that contains
   * information about the event that triggered the function. In this case, it is likely an event
   * object related to a window resize event, as the function is intended to handle resizing of the
   * window. The `event` parameter
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.zone.runOutsideAngular(() => {
      this.document.body.style.setProperty('--height', `${event.target.innerHeight}px`);
    })
  }

  /**
   * The function isSafari() checks if the user's browser is Safari by examining the userAgent string.
   * @returns The function is checking if the user agent string contains "safari" but does not contain
   * "chrome" or "android". If this condition is met, the function returns true, indicating that the
   * browser is Safari. If the condition is not met, the function returns false.
   */
  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  /**
   * The function `setCanonicalURL` sets the canonical URL and home URL in the document's head element
   * if they do not already exist.
   * @param {string} url - The `url` parameter in the `setCanonicalURL` function is a string that
   * represents the canonical URL that you want to set for the current document. This URL is used to
   * specify the preferred version of a web page to search engines and helps prevent duplicate content
   * issues.
   */
  private setCanonicalURL(url: string) {
    const existingLink: HTMLLinkElement | null = this.document.querySelector("link[rel='canonical']");
    if (existingLink) {
      existingLink.setAttribute('href', url);
    } else {
      const link: HTMLLinkElement = document.createElement('link');
      link.setAttribute('href', url);
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    const existingHomeLink: HTMLLinkElement | null = this.document.querySelector("link[rel='home']");
    if (!existingHomeLink) {
      const link: HTMLLinkElement = this.document.createElement('link');
      link.setAttribute('href', environment.SITE_URL);
      link.setAttribute('rel', 'home');
      this.document.head.appendChild(link);
    } else {
      existingHomeLink.setAttribute('href', environment.SITE_URL);
    }
  }

  /**
   * The function `activateUpdate` activates an update and reloads the document if an update is
   * available.
   */
  protected activateUpdate(): void {
    this.zone.runOutsideAngular(() => {
      this.swUpdate.activateUpdate().then((e) => {
        if (e) {
          this.updateAvailable.set(false);
          typeof this.document !== 'undefined' && this.document.location.reload()
        }
        timer(2000).subscribe(() => {
          this.updateAvailable() && (this.updateAvailable.set(false));
          typeof this.document !== 'undefined' && this.document.location.reload()
        })
      })
    })
  }
}
